// 1.5.10 (Alexander, 24–25 Oct) — M's arrival: the first login of the day, after Face ID, the crimson line runs in
// along the slant the M's three cuts lie on, traces the M's inner contour and runs out to the right edge; the M
// develops and lifts off the page, its shadow spreading below, as the line dissolves; then the home takes over.
// Timed to Alexander's reference film (mock-up "M® Arrival Splash", approved 25 Oct, "We nailed it"):
//   0 s          the sound and the line start together
//   0–1.14 s     the line at one constant speed, screen edge to screen edge
//   0.80–1.50 s  the M develops: first sign at 0.80, about a third at 1.05, full by 1.50
//   1.05–1.80 s  the M rises with the line on it; the shadow from 1.05
//   1.34–1.57 s  the line dissolves
//   1.80 s       the hand-off: the splash fades to the home (0.35 s) — once the home is there
// Tap to skip at any point: the sound fades in 0.15 s, the home comes up in 0.2 s.
// Only on a login (createView), never on the landing, a code, a note, a Drop, a request or any other link page.
// The drawing is code (the logo's own path); the sound is Alexander's (assets/m-arrive.mp3, 32 KB).
import { playSound, fadeSound, preloadSound } from "./sound";
import { resolveTheme } from "./theme";

const KEY = "m.arrived"; // the local date of the last arrival on this device
const M_PATH = "M538.4,209.9c-42.8-49.7-106.3-81.1-176.8-81.1-128.4,0-233.1,104.5-233.1,232.9v318.2l112.9-63.2v-255c0-66.3,54-120.2,120.2-120.2s120.2,53.9,120.2,120.2v120.2l112.9-63.2v-56.9c0-66.3,53.9-120.2,120.2-120.2s78.4,21.3,100,53.6l98.5-55.2c-41-66.6-114.6-111.2-198.5-111.2s-133.8,31.5-176.6,81.1Z";
const SLOPE = -63.2 / 112.9; // the cuts' slant (all three lie on one line through (128.5, 679.9))
const THEMES = {
  light: { ground: "#fbfaf8", fill: "#3d5afe", tint: "#c9d1ff", line: "#D11A2C", shade: "#1d2433", shadeOp: 0.34 },
  dark: { ground: "#010f33", fill: "#f5f7ff", tint: "#2b4a8a", line: "#ff3b4f", shade: "#000000", shadeOp: 0.6 },
} as const;
// 1.5.40 (Alexander, 29 Oct: "Perfect! Let's do the same change to the app's splashscreen") — on Midnight the arrival
// stands on the ground of the film's credits: Cobalt's waves (the theme's 600-pt tile) and a faint cobalt light behind the
// M. Pure keeps its flat paper.
const GROUND_IMAGE: Record<keyof typeof THEMES, string> = {
  light: "",
  dark: "background-image:url(/waves-cobalt-soft.svg?v=1),radial-gradient(ellipse 70% 38% at 50% 46%, rgba(9, 38, 91, 0.55) 0%, transparent 100%);background-size:600px 600px,100% 100%;background-repeat:repeat,no-repeat;background-position:center top,center;",
};
const T = { draw: 1140, dev0: 800, dev1: 1050, dev2: 1500, lift0: 1050, dim0: 1340, dim1: 1570, reveal: 1800, handoff: 350 };

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Is an arrival due — no arrival on this device today? (Storage blocked: never, rather than every login.) */
export function arrivalDue(): boolean {
  try {
    if (new URLSearchParams(location.search).has("splash")) return true; // Lab / harness: show it again
    return localStorage.getItem(KEY) !== today();
  } catch { return false; }
}

export function markArrived(): void {
  try { localStorage.setItem(KEY, today()); } catch { /* ignore */ }
}

/** At the login tap, when an arrival is due: fetch the sound while Face ID runs. */
export function prepareArrival(): void {
  if (arrivalDue()) preloadSound("arrive");
}

/**
 * Play the arrival over whatever is painted beneath (the home, rendering under it). `homeReady` says when the home is
 * there; the hand-off waits for it (at most 2 s more), so the splash never fades to the login screen.
 */
export function showArrival(homeReady: () => boolean = () => true): void {
  markArrived();
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const theme = resolveTheme(), th = THEMES[theme];
  const W = Math.max(320, window.innerWidth), H = Math.max(480, window.innerHeight);
  const S = 180 / 821; // the M 180 px wide, as on the login
  const tx = W / 2 - (110 + 410.5) * S, ty = H * 0.465 - (111 + 293.5) * S;
  // the line in the logo's units, from the screen's left edge to its right
  const x0 = (-4 - tx) / S, x1 = (W + 4 - tx) / S, y = (x: number) => 679.9 + SLOPE * (x - 128.5);
  const d = `M ${x0.toFixed(1)} ${y(x0).toFixed(1)} L 128.5 679.9 L 241.4 616.7 L 241.4 361.7 C 241.4 295.4 295.4 241.5 361.6 241.5 C 427.8 241.5 481.8 295.4 481.8 361.7 L 481.8 481.9 L 594.7 418.7 L 594.7 361.8 C 594.7 295.5 648.6 241.6 714.9 241.6 C 781.2 241.6 793.3 262.9 814.9 295.2 L ${x1.toFixed(1)} ${y(x1).toFixed(1)}`;

  const el = document.createElement("div");
  el.id = "arrival";
  el.setAttribute("aria-hidden", "true");
  el.style.cssText = `position:fixed;inset:0;z-index:2147483000;background-color:${th.ground};${GROUND_IMAGE[theme]}cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:none`;
  el.innerHTML = `<svg width="100%" height="100%" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice" style="display:block">
    <defs><filter id="arrSoft" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="16"/></filter></defs>
    <g transform="translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${S})">
      <g class="arr-shadow" style="opacity:0"><path filter="url(#arrSoft)" fill="${th.shade}" d="${M_PATH}"/></g>
      <g class="arr-lift">
        <path class="arr-fill" fill="${th.tint}" style="opacity:0" d="${M_PATH}"/>
        <path class="arr-line" fill="none" stroke="${th.line}" stroke-width="${(2.2 / S).toFixed(2)}" stroke-linecap="round" stroke-linejoin="round" d="${d}"/>
      </g>
    </g></svg>`;
  document.body.appendChild(el);
  const $ = (s: string) => el.querySelector<SVGGraphicsElement>(s)!;
  const line = $(".arr-line") as SVGPathElement, fill = $(".arr-fill"), lift = $(".arr-lift"), shadow = $(".arr-shadow");
  const len = line.getTotalLength();
  line.style.strokeDasharray = `${len} ${len}`;
  line.style.strokeDashoffset = String(len);

  const anims: Animation[] = [];
  let ended = false, timer = 0;
  const end = (ms: number) => {
    if (ended) return;
    ended = true;
    clearTimeout(timer);
    const from = getComputedStyle(el).opacity;
    anims.forEach((a) => a.pause());
    const out = el.animate([{ opacity: from }, { opacity: 0 }], { duration: ms, fill: "forwards" });
    out.onfinish = () => el.remove();
    setTimeout(() => el.remove(), ms + 200); // in case animations are throttled
  };
  el.addEventListener("pointerdown", (e) => { e.preventDefault(); e.stopPropagation(); fadeSound("arrive", 150); end(200); }); // tap to skip

  const run = () => {
    if (ended || anims.length) return;
    const R = T.reveal, o = (ms: number) => ms / R, E = "cubic-bezier(0.3, 0.2, 0.4, 1)";
    const UP = "translate(-10px, -26px)", DOWN = "translate(26px, 46px)"; // in the logo's units (1 ≈ 0.22 px)
    anims.push(line.animate([{ strokeDashoffset: len }, { strokeDashoffset: 0 }], { duration: T.draw, easing: "linear", fill: "forwards" }));
    anims.push(line.animate([{ opacity: 1 }, { opacity: 1, offset: o(T.dim0) }, { opacity: 0, offset: o(T.dim1) }, { opacity: 0 }], { duration: R, fill: "forwards" }));
    anims.push(fill.animate([
      { opacity: 0, fill: th.tint }, { opacity: 0, fill: th.tint, offset: o(T.dev0) },
      { opacity: 0.35, fill: th.tint, offset: o(T.dev1) }, { opacity: 1, fill: th.fill, offset: o(T.dev2) }, { opacity: 1, fill: th.fill },
    ], { duration: R, fill: "forwards" }));
    anims.push(lift.animate([{ transform: "translate(0px, 0px)" }, { transform: "translate(0px, 0px)", offset: o(T.lift0), easing: E }, { transform: UP }], { duration: R, fill: "forwards" }));
    anims.push(shadow.animate([
      { transform: "translate(0px, 0px)", opacity: 0 }, { transform: "translate(0px, 0px)", opacity: 0, offset: o(T.lift0), easing: E },
      { transform: DOWN, opacity: th.shadeOp },
    ], { duration: R, fill: "forwards" }));
    const t0 = performance.now();
    const handOff = () => {
      if (ended) return;
      if (homeReady() || performance.now() - t0 > R + 2000) end(T.handoff);
      else timer = window.setTimeout(handOff, 60);
    };
    timer = window.setTimeout(handOff, R);
  };
  // the line and the sound start together: the drawing waits for the sound to start (at most 350 ms)
  void playSound("arrive", 350).then(run, run);
  window.setTimeout(run, 400);
}
